import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { incomeList } from "../json/incomeList";
import { Autocomplete, TextField } from "@mui/material";
import { professionList } from "../json/professionList";
import { qualificationList } from "../json/qualificationList";
import { dietList } from "../json/dietList";
import { communityList } from "../json/communityList";
import { heightList } from "../json/heightList";
import { maritalStatusList } from "../json/maritalStatusList";
import { motherTongueList } from "../json/motherTongueList";
import { religionList } from "../json/religionList";
import { stateCityList } from "../json/stateCityList";
import { workingInList } from "../json/workingInList";
import { presigned, server } from "../server";

const MAX_IMAGES = 10;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5 MB
const COUNT_START_STEP = 2; // Contact Information
const COUNT_END_STEP = 6; // Family Background (last counted step)

const PROFILE_OPTIONS = [
  "Self",
  "Son",
  "Daughter",
  "Brother",
  "Sister",
  "Friend",
  "Relative",
];
const GENDER_OPTIONS = ["Male", "Female"];
const AUTO_GENDER_MAP = {
  Son: "Male",
  Brother: "Male",
  Daughter: "Female",
  Sister: "Female",
};

const MuiAutocomplete = ({
  label,
  options,
  value,
  onChange,
  disabled = false,
}) => (
  <Autocomplete
    options={options}
    getOptionLabel={(option) => option.Name}
    value={options.find((o) => o.Name === value) || null}
    onChange={(e, newValue) => onChange(newValue ? newValue.Name : "")}
    disabled={disabled}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.5rem",
          },
        }}
      />
    )}
  />
);

const MuiTextInput = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  error = false,
  helperText = "",
  disabled = false,
  inputProps = {},
}) => (
  <TextField
    fullWidth
    size="small"
    sx={{
      "& .MuiOutlinedInput-root": {
        borderRadius: "0.5rem",
      },
    }}
    label={label}
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    error={error}
    helperText={helperText}
    disabled={disabled}
    inputProps={inputProps}
  />
);

export default function Register({ onClose }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [savingMessage, setSavingMessage] = useState("");
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [mobileExists, setMobileExists] = useState(false);

  const dobToMillis = (dobStr) => {
    if (!dobStr) return null;

    // input type="date" gives YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
      const [y, m, d] = dobStr.split("-").map(Number);
      return Date.UTC(y, m - 1, d); // always midnight UTC
    }

    // support dd/mm/yyyy (if you ever use it)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) {
      const [d, m, y] = dobStr.split("/").map(Number);
      return Date.UTC(y, m - 1, d);
    }

    return null;
  };

  const [form, setForm] = useState({
    // Step 1
    profileFor: "",
    gender: "",

    // Step 2
    name: "",
    dob: "", // dd/mm/yyyy OR ISO (we'll accept ISO-type date input)
    height: "",

    // Step 3
    email: "",
    mobile: "",

    // Step 4
    religion: "",
    motherTongue: "",
    community: "",

    // Step 5
    maritalStatus: "",
    diet: "",
    state: "",
    city: "",

    // Step 6
    qualification: "",
    workingIn: "",
    profession: "",
    annualIncome: "",

    // Step 7
    familyType: "",
    fatherStatus: "",
    motherStatus: "",

    // Step 8
    images: [], // { file, preview, name }
  });

  useEffect(() => {
    const controller = new AbortController();

    const checkMobileExists = async () => {
      if (!/^\d{10}$/.test(form.mobile)) {
        setMobileExists(false);
        return;
      }

      try {
        setCheckingMobile(true);

        const res = await fetch(`${server}/CheckMobile?mobile=${form.mobile}`, {
          signal: controller.signal,
        });

        const data = await res.json();

        if (data?.message === "Mobile number already exists") {
          setMobileExists(true);
        } else {
          setMobileExists(false);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Mobile check failed", err);
        }
      } finally {
        setCheckingMobile(false);
      }
    };

    checkMobileExists();

    return () => controller.abort();
  }, [form.mobile]);

  // Load saved draft
  useEffect(() => {
    try {
      const saved = localStorage.getItem("register");
      if (saved) {
        const parsed = JSON.parse(saved);
        // images cannot be restored to File objects; keep preview urls if available
        const images = (parsed.images || []).map((img) => ({
          file: null,
          preview: img.preview || img.url || null,
          name: img.name || "",
        }));
        setForm((prev) => ({ ...prev, ...parsed, images }));
      }
    } catch (e) {
      console.warn("Failed to load saved register:", e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist draft
  useEffect(() => {
    try {
      const toStore = { ...form };
      // don't attempt to store File objects — save minimal image data
      toStore.images = form.images.map((i) => ({
        name: i.name || "",
        preview: i.preview || null,
      }));
      localStorage.setItem("register", JSON.stringify(toStore));
    } catch (e) {
      console.warn("Failed to save register to localStorage:", e);
    }
  }, [form]);

  // helpers
  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const isCountedStep = step >= COUNT_START_STEP && step <= COUNT_END_STEP;
  const displayStepNumber = isCountedStep ? step - COUNT_START_STEP + 1 : null;
  const totalCountedSteps = COUNT_END_STEP - COUNT_START_STEP + 1;

  const isValidDate = (dateStr) => {
    const isoMatch = /^\d{4}-\d{2}-\d{2}$/;
    if (isoMatch.test(dateStr)) {
      const [y, m, d] = dateStr.split("-");
      const dt = new Date(`${y}-${m}-${d}`);
      return (
        dt &&
        dt.getFullYear() === parseInt(y) &&
        dt.getMonth() + 1 === parseInt(m) &&
        dt.getDate() === parseInt(d)
      );
    }
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(regex);
    if (!match) return false;
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    const d = new Date(year, month, day);
    return (
      d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
    );
  };

  const getAgeFromDob = (dobStr) => {
    if (!dobStr) return null;
    // accept ISO or dd/mm/yyyy
    let birth;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dobStr)) {
      birth = new Date(dobStr);
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) {
      const [d, m, y] = dobStr.split("/");
      birth = new Date(`${y}-${m}-${d}`);
    } else return null;
    if (!birth || isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const mDiff = today.getMonth() - birth.getMonth();
    if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // step validations
  const validStep1 =
    !!form.profileFor &&
    (AUTO_GENDER_MAP[form.profileFor] ? true : !!form.gender);
  const validStep2 =
    form.name.trim() !== "" &&
    isValidDate(form.dob) &&
    (() => {
      const age = getAgeFromDob(form.dob);
      return age !== null && age >= 18 && age <= 70;
    })() &&
    !!form.height;
  const isValidEmail = (e) => /\S+@\S+\.\S+/.test(e);
  const isValidMobile = (m) => /^\d{10}$/.test(m);
  const validStep3 =
    isValidEmail(form.email) &&
    isValidMobile(form.mobile) &&
    !mobileExists &&
    !checkingMobile;
  const validStep4 = !!form.religion && !!form.motherTongue && !!form.community;
  const validStep5 = !!form.maritalStatus && !!form.diet;
  const validStep6 = !!form.state && !!form.city;
  const validStep7 =
    !!form.qualification &&
    !!form.workingIn &&
    (form.workingIn === "Not Working" ? true : !!form.annualIncome);
  const validStep8 =
    !!form.familyType && !!form.fatherStatus && !!form.motherStatus;
  // photos optional; set to true even if zero images. change to form.images.length>0 if you want required
  const validLastStep = true; // photos optional

  // navigation
  const goNext = async () => {
    if (creating) return;

    if (step === 0 && !validStep1) return;
    if (step === 1 && !validStep2) return;
    if (step === 2 && !validStep3) return;
    if (step === 3 && !validStep4) return;
    if (step === 4 && !validStep5) return;
    if (step === 5 && !validStep6) return;
    if (step === 6 && !validStep7) return;

    // 🚀 FINAL STEP → SUBMIT
    if (step === 7) {
      await handleSubmit();
      return;
    }

    // normal step advance
    setStep((s) => Math.min(7, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    if (creating) return;
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // image selection (square boxes)
  const onFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f.type.startsWith("image/")) continue;
      if (f.size > MAX_IMAGE_SIZE) {
        alert(`${f.name} is larger than 5MB and was skipped.`);
        continue;
      }
      if (form.images.length + allowed.length >= MAX_IMAGES) {
        alert(`Maximum ${MAX_IMAGES} images allowed.`);
        break;
      }
      allowed.push({ file: f, preview: URL.createObjectURL(f), name: f.name });
    }
    if (allowed.length > 0)
      setForm((p) => ({ ...p, images: [...p.images, ...allowed] }));
    // reset input so same file can be chosen again if removed
    e.target.value = "";
  };

  const removeImage = (idx) => {
    // revoke object URL to avoid memory leak
    const img = form.images[idx];
    if (img && img.preview && img.file) URL.revokeObjectURL(img.preview);
    setForm((p) => ({ ...p, images: p.images.filter((_, i) => i !== idx) }));
  };

  // S3 presigned upload & signup flow (uses fetch, not axios)
  const uploadImagesToS3 = async () => {
    if (!form.images || form.images.length === 0) return [];
    // Create unique file names
    const fileNames = form.images.map(
      (i, idx) => `web_${Date.now()}_${idx}_${i.name || "img.jpg"}`
    );

    // Request presigned urls from backend
    const resp = await fetch(`${presigned}/GenerateImagePresigned`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ folder: "profile", fileNames }),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Presigned request failed: ${resp.status} ${text}`);
    }
    const presignedData = await resp.json();
    const urls = presignedData.urls || [];

    // Upload each file via PUT to signedUrl
    for (let i = 0; i < form.images.length; i++) {
      const file = form.images[i].file;
      const signedUrl = urls[i]?.signedUrl;
      if (!signedUrl)
        throw new Error("Missing signedUrl from presigned response");
      const putResp = await fetch(signedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "image/jpeg" },
        body: file,
      });
      if (!putResp.ok) {
        const text = await putResp.text();
        throw new Error(
          `Upload failed for ${form.images[i].name}: ${putResp.status} ${text}`
        );
      }
    }

    // return final public urls or keys
    return urls.map((u) => u.url || u.publicUrl || u.key || u.signedUrl);
  };

  const handleSubmit = async () => {
    if (creating) return;
    // final validation before submit
    if (
      !validStep1 ||
      !validStep2 ||
      !validStep3 ||
      !validStep4 ||
      !validStep5 ||
      !validStep6 ||
      !validStep7 ||
      !validStep8
    ) {
      alert("Please complete required fields before submitting.");
      return;
    }

    try {
      setCreating(true);
      setSavingMessage("Uploading Images");
      const uploadedUrls = await uploadImagesToS3();
      setSavingMessage("Creating Account");

      const imagesPayload = (uploadedUrls || []).map((u) => ({
        docUrl: u,
        verified: false,
      }));

      // Prepare payload shape same as mobile but platform 'web'
      const payload = {
        profileFor: form.profileFor,
        gender: form.gender,
        name: form.name,
        platform: "web",
        email: form.email,
        mobile: Number(form.mobile),
        deviceToken: "",

        profile: {
          dob: dobToMillis(form.dob),
          height: form.height,
          maritalStatus: form.maritalStatus,
          religion: form.religion,
          motherTongue: form.motherTongue,
          community: form.community,
          diet: form.diet,
          state: form.state,
          city: form.city,
          pincode: "",
          qualification: form.qualification,
          collegeName: "",
          profession: form.profession,
          workingIn: form.workingIn,
          companyName: "",
          annualIncome: form.annualIncome,
          familyType: form.familyType,
          fatherStatus: form.fatherStatus,
          motherStatus: form.motherStatus,
          noOfBrothers: "",
          noOfSisters: "",
          noOfMarriedBrothers: "",
          noOfMarriedSisters: "",
          familyLivingIn: "",
        },

        preferences: {
          minAge: 21,
          maxAge: 35,
          minHeight: 48,
          maxHeight: 84,
          maritalStatus: ["Open to All"],
          diet: ["Open to All"],
          religion: ["Open to All"],
          community: ["Open to All"],
          motherTongue: ["Open to All"],
          state: ["Open to All"],
          city: ["Open to All"],
          qualification: ["Open to All"],
          workingIn: ["Open to All"],
          profession: ["Open to All"],
          annualIncome: ["Open to All"],
          familyType: ["Open to All"],
        },

        images: imagesPayload,
      };

      const signupResp = await fetch(`${server}/auth/Signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!signupResp.ok) {
        const text = await signupResp.text();
        throw new Error(`Signup failed: ${signupResp.status} ${text}`);
      }
      const signupData = await signupResp.json();
      // success
      localStorage.removeItem("register");
      // ✅ Close modal FIRST
      if (onClose) {
        onClose();
      }

      // ✅ Manually clean up Bootstrap leftovers
      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      // remove any lingering backdrops
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

      // ✅ Navigate AFTER cleanup
      navigate("/verify-otp", {
        replace: true,
        state: {
          mobile: form.mobile,
          signupResponse: signupData,
        },
      });
    } catch (err) {
      alert("Submission failed: " + (err.message || err));
      setCreating(false);
      setSavingMessage("");
    }
  };

  // --- UI pieces for each step (styled with Bootstrap classes) ---
  const StepHeader = ({ title, subtitle }) => (
    <div className="mb-3">
      <h4 className="mb-0 lh-1">{title}</h4>
      {subtitle && <p className="text-muted m-0 lh-1">{subtitle}</p>}
    </div>
  );

  // small helper to render pill options (profile/gender etc)
  const Pill = ({ active, onClick, children }) => (
    <button
      type="button"
      className={`btn btn-sm border-light px-4 fw-medium rounded-pill ${
        active ? "btn-info text-white" : "btn-outline-secondary"
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );

  const filteredCommunities = form.religion
    ? communityList
        .filter((c) => c.Name.startsWith(form.religion + "-"))
        .map((c) => ({
          ...c,
          Name: c.Name.replace(form.religion + "-", ""),
        }))
    : [];

  const stateOptions = Array.from(
    new Map(stateCityList.map((i) => [i.State, { Name: i.State }])).values()
  );

  const cityOptions = form.state
    ? stateCityList
        .filter((i) => i.State === form.state)
        .map((i) => ({ Name: i.City }))
    : [];

  return (
    <>
      <div className="p-4">
        <div className="d-flex align-items-center mb-3">
          <div>
            {step > 0 && (
              <span
                onClick={goBack}
                disabled={creating}
                className="icon-shape me-2 icon-md bg-light-red rounded-circle cursor-pointer position-relative z-1"
              >
                <i className="bi bi-chevron-left fs-4 text-primary"></i>
              </span>
            )}
          </div>
          <img src="/assets/images/profile.png" width="70" />
          {step > 0 && (
            <div className="d-flex align-items-center ms-3">
              <div>
                <p className="text-muted lh-sm m-0">
                  {isCountedStep && (
                    <span className="text-dark">
                      Step {displayStepNumber} of {totalCountedSteps}
                    </span>
                  )}
                  <br />
                  {isCountedStep
                    ? `Progress — ${Math.round(
                        (displayStepNumber / totalCountedSteps) * 100
                      )}%`
                    : null}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* content card */}
        <div>
          <div>
            {step === 0 && (
              <>
                <StepHeader
                  title="This Profile is for"
                  subtitle="Select who the profile is for and gender"
                />

                <div className="mb-3 d-flex flex-wrap gap-1 me-lg-7 me-4">
                  {PROFILE_OPTIONS.map((p) => (
                    <Pill
                      key={p}
                      active={form.profileFor === p}
                      onClick={() => {
                        const autoGender = AUTO_GENDER_MAP[p] || "";
                        setForm((f) => ({
                          ...f,
                          profileFor: p,
                          gender: autoGender,
                        }));
                      }}
                    >
                      {p}
                    </Pill>
                  ))}
                </div>

                {!AUTO_GENDER_MAP[form.profileFor] && form.profileFor && (
                  <>
                    <h6 className="mt-3">Gender</h6>
                    <div className="d-flex gap-2 mt-2">
                      {GENDER_OPTIONS.map((g) => (
                        <Pill
                          key={g}
                          active={form.gender === g}
                          onClick={() => update("gender", g)}
                        >
                          {g}
                        </Pill>
                      ))}
                    </div>
                  </>
                )}

                <div className="alert alert-warning my-5 d-flex align-items-center rounded-4">
                  <div className="d-flex align-items-center px-4 py-1">
                    <img
                      src="/assets/images/secure.png"
                      width="40"
                      alt="secure"
                    />
                    <div className="ms-3">
                      <h5 className="m-0 lh-1 mb-1">
                        GoRishtey.com is built for genuine match-seekers.
                      </h5>
                      <p className="m-0 lh-sm">
                        Any falsification, commercial use or marriage bureaus is
                        strictly prohibited &amp; may be reported to law
                        enforcement.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <StepHeader
                  title="Personal Details"
                  subtitle="Enter name, DOB and height"
                />
                <div className="mb-3">
                  <label className="form-label">Full Name</label>
                  <MuiTextInput
                    label="Full Name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Date of Birth</label>
                  <MuiTextInput
                    label="Date of Birth"
                    type="date"
                    value={form.dob}
                    onChange={(e) => update("dob", e.target.value)}
                    error={form.dob && !isValidDate(form.dob)}
                    helperText={
                      form.dob && !isValidDate(form.dob) ? "Invalid date" : ""
                    }
                  />

                  {form.dob && !isValidDate(form.dob) && (
                    <small className="text-danger">Invalid date</small>
                  )}

                  {form.dob &&
                    (() => {
                      const age = getAgeFromDob(form.dob);
                      if (age !== null && (age < 18 || age > 70)) {
                        return (
                          <small className="text-danger">
                            Age must be between 18 and 70.
                          </small>
                        );
                      }
                      return null;
                    })()}
                </div>

                <div className="mb-3">
                  <label className="form-label">Height</label>
                  <MuiAutocomplete
                    label="Height"
                    options={heightList}
                    value={form.height}
                    onChange={(v) => update("height", v)}
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <StepHeader
                  title="Contact Information"
                  subtitle="Provide active email and mobile"
                />
                <div className="mb-3">
                  <label className="form-label">Email Address</label>
                  <MuiTextInput
                    label="Email ID"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    error={!!form.email && !isValidEmail(form.email)}
                    helperText={
                      form.email && !isValidEmail(form.email)
                        ? "Invalid email"
                        : ""
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Mobile No.</label>
                  <MuiTextInput
                    label="Mobile Number"
                    value={form.mobile}
                    onChange={(e) =>
                      update(
                        "mobile",
                        e.target.value.replace(/\D/g, "").slice(0, 10)
                      )
                    }
                    error={
                      (!!form.mobile && !isValidMobile(form.mobile)) ||
                      mobileExists
                    }
                    helperText={
                      mobileExists
                        ? "Mobile number already exists"
                        : form.mobile && !isValidMobile(form.mobile)
                        ? "Enter 10 digit mobile number"
                        : checkingMobile
                        ? "Checking Mobile Number ..."
                        : ""
                    }
                    inputProps={{ maxLength: 10 }}
                  />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <StepHeader
                  title="Cultural Background"
                  subtitle="Religion, mother tongue & community"
                />
                <div className="mb-3">
                  <label className="form-label">Religion</label>
                  <MuiAutocomplete
                    label="Religion"
                    options={religionList}
                    value={form.religion}
                    onChange={(v) => {
                      update("religion", v);
                      update("community", "");
                    }}
                  />
                </div>

                {form.religion && (
                  <div className="mb-3">
                    <label className="form-label">Community</label>
                    <MuiAutocomplete
                      label="Community"
                      options={filteredCommunities}
                      value={form.community}
                      onChange={(v) => update("community", v)}
                      disabled={!form.religion}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Mother Tongue</label>
                  <MuiAutocomplete
                    label="Mother Tongue"
                    options={motherTongueList}
                    value={form.motherTongue}
                    onChange={(v) => update("motherTongue", v)}
                  />
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <StepHeader
                  title="Lifestyle & Location Information"
                  subtitle="State & City"
                />

                <div className="mb-3">
                  <label className="form-label">Marital Status</label>
                  <MuiAutocomplete
                    label="Marital Status"
                    options={maritalStatusList}
                    value={form.maritalStatus}
                    onChange={(v) => update("maritalStatus", v)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Your Diet</label>
                  <MuiAutocomplete
                    label="Diet"
                    options={dietList}
                    value={form.diet}
                    onChange={(v) => update("diet", v)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">State</label>
                  <MuiAutocomplete
                    label="State"
                    options={stateOptions}
                    value={form.state}
                    onChange={(v) => {
                      update("state", v);
                      update("city", "");
                    }}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">City</label>
                  <MuiAutocomplete
                    label="City"
                    options={cityOptions}
                    value={form.city}
                    onChange={(v) => update("city", v)}
                    disabled={!form.state}
                  />
                </div>
              </>
            )}

            {step === 5 && (
              <>
                <StepHeader
                  title="Professional Information"
                  subtitle="Qualification, work & income"
                />
                <div className="mb-3">
                  <label className="form-label">Qualification</label>
                  <MuiAutocomplete
                    label="Qualification"
                    options={qualificationList}
                    value={form.qualification}
                    onChange={(v) => update("qualification", v)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Working In</label>
                  <MuiAutocomplete
                    label="Working In"
                    options={workingInList}
                    value={form.workingIn}
                    onChange={(v) => {
                      update("workingIn", v);
                      update("profession", "");
                      update("annualIncome", "");
                    }}
                  />
                </div>

                {form.workingIn && form.workingIn !== "Not Working" && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Profession</label>
                      <MuiAutocomplete
                        label="Profession"
                        options={professionList}
                        value={form.profession}
                        onChange={(v) => update("profession", v)}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Annual Income</label>
                      <MuiAutocomplete
                        label="Annual Income"
                        options={incomeList}
                        value={form.annualIncome}
                        onChange={(v) => update("annualIncome", v)}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {step === 6 && (
              <>
                <StepHeader
                  title="Family Background"
                  subtitle="Family type and parents' status"
                />
                <div className="mb-3">
                  <label className="form-label">Family Type</label>
                  <MuiAutocomplete
                    label="Family Type"
                    options={[
                      { Name: "Joint Family" },
                      { Name: "Nuclear Family" },
                      { Name: "Other" },
                    ]}
                    value={form.familyType}
                    onChange={(v) => update("familyType", v)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Father Status</label>
                  <MuiAutocomplete
                    label="Father Status"
                    options={[
                      { Name: "Employed" },
                      { Name: "Retired" },
                      { Name: "Business" },
                      { Name: "Not Employed" },
                      { Name: "Passed Away" },
                    ]}
                    value={form.fatherStatus}
                    onChange={(v) => update("fatherStatus", v)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Mother Status</label>
                  <MuiAutocomplete
                    label="Mother Status"
                    options={[
                      { Name: "Homemaker" },
                      { Name: "Employed" },
                      { Name: "Retired" },
                      { Name: "Business" },
                      { Name: "Passed Away" },
                    ]}
                    value={form.motherStatus}
                    onChange={(v) => update("motherStatus", v)}
                  />
                </div>
              </>
            )}

            {step === 7 && (
              <>
                <StepHeader
                  title="Upload Photos"
                  subtitle="Upload up to 10 photos (≤ 5MB each)"
                />
                <div className="mb-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onFilesSelected}
                    disabled={form.images.length >= MAX_IMAGES}
                  />
                </div>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {form.images.map((img, idx) => (
                    <div key={idx} style={{ width: 95 }}>
                      <div
                        className="position-relative"
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: 8,
                          overflow: "hidden",
                          border: "1px solid #e9e9e9",
                        }}
                      >
                        <img
                          src={img.preview || img.url}
                          alt={img.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <small
                          className="text-muted text-truncate"
                          style={{ maxWidth: 70 }}
                        >
                          {img.name || "Photo"}
                        </small>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removeImage(idx)}
                          disabled={creating}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}

                  {form.images.length < MAX_IMAGES && (
                    <div style={{ width: 95 }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: 8,
                          border: "1px dashed #ced4da",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            fileInputRef.current && fileInputRef.current.click()
                          }
                        >
                          Add
                        </button>
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">Add photo</small>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* navigation buttons */}
        {!creating && (
          <div className="d-flex justify-content-center align-items-center mt-4">
            <div>
              {step < 8 && (
                <button
                  className="btn bg-red-primary fw-medium text-white rounded-pill px-7"
                  onClick={goNext}
                  disabled={
                    (step === 0 && !validStep1) ||
                    (step === 1 && !validStep2) ||
                    (step === 2 && !validStep3) ||
                    (step === 3 && !validStep4) ||
                    (step === 4 && !validStep5) ||
                    (step === 5 && !validStep6) ||
                    (step === 6 && !validStep7) ||
                    (step === 7 && !validStep8)
                  }
                >
                  {step === 7 ? "Continue / Skip Without Image" : "Continue"}
                </button>
              )}
            </div>
          </div>
        )}

        {creating && (
          <div className="d-flex justify-content-center mt-4">
            <div className="text-center">
              <div className="spinner-border text-info" role="status" />
              <div className="mt-2 text-muted">
                {savingMessage || "Creating Account"}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
